import React, { useEffect, useState } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { API_URL, STRIPE_PUBLIC_KEY } from "../../constants/formConstants";
import { getAcquisitionCountryMetadataFromSearch } from "../../utils/acquisitionCountry";
import { getCheckoutMetadataFromSearch as getUtmMetadataFromSearch } from "../../utils/checkoutMetadata";
import { UPSELL_OFFERS } from "../../constants/upsells";
import { redirectToPostPurchaseAppEntry } from "../../utils/postPurchaseRedirect";
import icon111Image from "../../assets/images/icon111.jpeg";
import icon22Image from "../../assets/images/icon22.jpeg";
import icon3Image from "../../assets/images/icon3.jpeg";
import { pushPurchaseEvent } from "../../utils/gtm";
import { getClientReferenceId, sendAmplitudePurchase } from "../../utils/amplitude";
import { sendPaywallEvent } from "../../utils/paywallAnalytics";

const offers = [
  {
    id: "combo",
    title: "Complete Look Bundle (Hair + Outfits + Try-On)",
    oldPrice: "$40.00",
    newPrice: "$19.99 only!",
    badge: "50% OFF",
    description: "",
    iconBg: "var(--funnel-cs-accent-soft)",
    iconColor: "var(--funnel-cs-accent)",
    image: icon111Image,
  },
  {
    id: "hair",
    title: "Hair Color and Hairstyles Analysis with Try-On",
    oldPrice: "$22.00",
    newPrice: "$14.99",
    badge: "32% OFF",
    description: "Get your best Hair colors & Hairstyles",
    iconBg: "var(--funnel-cs-field)",
    iconColor: "var(--funnel-cs-accent)",
    image: icon22Image,
  },
  {
    id: "outfits",
    title: "Outfits for Your Color Type with Try-On",
    oldPrice: "$22.00",
    newPrice: "$14.99",
    badge: "32% OFF",
    description: "Get Outfits for your color type with Try On",
    iconBg: "#fff1e8",
    iconColor: "#7a3f22",
    image: icon3Image,
  },
];

const benefits = [
  "One-time payment, no surprise renewals.",
  "Smart guidance: outfits and hair by your season.",
  "Virtual try-on for hair and outfits before you commit.",
];

const IndicatorIcon = ({ selected }) => {
  if (selected) {
    return (
      <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-[var(--funnel-cs-accent)] text-[var(--funnel-cs-on-accent)] flex items-center justify-center shadow-sm">
        ✓
      </div>
    );
  }
  return (
    <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full border border-[var(--funnel-cs-border)] flex items-center justify-center bg-[var(--funnel-cs-surface)]">
      <div className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[var(--funnel-cs-field)]" />
    </div>
  );
};

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const GetOffer = () => {
  const [selected, setSelected] = useState(offers[0].id);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    sendPaywallEvent("upsell_seen", { route: "/getoffer" });
  }, []);

  const goRedirect = () => {
    redirectToPostPurchaseAppEntry(window.location.search);
  };

  const handleSkip = () => {
    sendPaywallEvent("upsell_skipped", {
      route: "/getoffer",
      payload: { offer_id: selected },
    });
    goRedirect();
  };

  const handleOfferSelect = (offerId) => {
    setSelected(offerId);
    sendPaywallEvent("upsell_offer_selected", {
      route: "/getoffer",
      payload: { offer_id: offerId },
    });
  };

  const handleGetOffer = async () => {
    if (loading) return;
    sendPaywallEvent("upsell_cta_clicked", {
      route: "/getoffer",
      payload: { offer_id: selected },
    });
    setLoading(true);
    try {
      const offer = UPSELL_OFFERS[selected];
      if (!offer) {
        goRedirect();
        return;
      }

      let customerId = null;
      let paymentMethodId = null;
      try {
        customerId = sessionStorage.getItem("stripeCustomerId");
        paymentMethodId = sessionStorage.getItem("stripePaymentMethodId");
      } catch (_) {}

      if (!customerId || !paymentMethodId) {
        goRedirect();
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) {
        goRedirect();
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      const clientReferenceId = searchParams.get("client_reference_id");
      const resolvedClientReferenceId = getClientReferenceId();
      const utmMetadata = getUtmMetadataFromSearch(window.location.search, { maxKeys: 47 });
      const lastPaywall = (() => {
        try {
          return sessionStorage.getItem("lastPaywall") || "getoffer";
        } catch (_) {
          return "getoffer";
        }
      })();
      const offerValue = (() => {
        const found = offers.find((o) => o.id === selected);
        if (!found?.newPrice) return undefined;
        const parsed = Number((found.newPrice || "").replace(/[^0-9.]/g, ""));
        return Number.isFinite(parsed) ? parsed : undefined;
      })();

      const pushUpsellEvent = async () => {
        try {
          if (resolvedClientReferenceId) {
            sessionStorage.setItem("clientReferenceId", resolvedClientReferenceId);
          }
        } catch (_) {}
        await pushPurchaseEvent({
          clientReferenceId: resolvedClientReferenceId,
          paywall: lastPaywall,
          value: offerValue,
          type: "upsell",
          extraKey: offer.offerId,
        });
        await sendAmplitudePurchase({
          userId: resolvedClientReferenceId,
          revenue: offerValue,
          currency: "usd",
          product: "Upsell combo",
          paywall: lastPaywall,
          type: "upsell",
          extraKey: offer.offerId,
        });
      };

      const body = {
        offerId: offer.offerId,
        priceId: offer.priceId,
        customerId,
        paymentMethodId,
        data: {
          currency: "usd",
          client_reference_id: resolvedClientReferenceId || clientReferenceId,
          paywall: lastPaywall,
          ...utmMetadata,
          ...getAcquisitionCountryMetadataFromSearch(window.location.search),
        },
      };

      const idempotencyKey = `upsell-${offer.offerId}-${customerId}-${Date.now()}`;

      const response = await axios.post(`${API_URL}/upsell-payment`, body, {
        headers: { "Idempotency-Key": idempotencyKey },
      });

      if (response?.data?.success) {
        sendPaywallEvent("upsell_purchase_completed", {
          route: "/getoffer",
          payload: {
            currency: "usd",
            offer_id: offer.offerId,
            status: "succeeded",
          },
        });
        await pushUpsellEvent();
        goRedirect();
        return;
      }

      if (response?.data?.requires_action && response?.data?.client_secret) {
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          response.data.client_secret
        );
        if (confirmError) {
          goRedirect();
          return;
        }
        const status = paymentIntent && paymentIntent.status;
        if (
          status === "succeeded" ||
          status === "processing" ||
          status === "requires_capture"
        ) {
          sendPaywallEvent("upsell_purchase_completed", {
            route: "/getoffer",
            payload: {
              currency: "usd",
              offer_id: offer.offerId,
              status,
            },
          });
          await pushUpsellEvent();
          goRedirect();
          return;
        }
        goRedirect();
        return;
      }

      goRedirect();
    } catch (_) {
      goRedirect();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--funnel-cs-bg)] flex justify-center px-3 py-3 sm:py-6 font-[var(--funnel-cs-font-body)] text-[var(--funnel-cs-ink)]">
      <div className="getoffer w-full max-w-[480px] bg-[var(--funnel-cs-surface)] rounded-[var(--funnel-cs-radius-card)] shadow-[var(--funnel-cs-shadow-card)] overflow-hidden border border-[var(--funnel-cs-border)]">
        {/* Header */}
        <div className="px-4 py-2 sm:py-3 border-b border-[var(--funnel-cs-border)]">
          <div className="text-base sm:text-lg font-semibold text-[var(--funnel-cs-ink)]">My Stylist</div>
        </div>

        {/* Stepper */}
        <div className="px-4 pt-2 pb-1.5 sm:pt-4 sm:pb-3">
          <div className="flex items-center justify-between text-xs sm:text-sm text-[var(--funnel-cs-muted)]">
            <div className="flex flex-col items-center flex-1">
              <div className="h-5 w-5 sm:h-8 sm:w-8 rounded-full border border-[var(--funnel-cs-accent-border)] bg-[var(--funnel-cs-accent-soft)] flex items-center justify-center text-[var(--funnel-cs-accent)] font-semibold text-[10px] sm:text-inherit">
                ✓
              </div>
              <span className="mt-0.5 sm:mt-1 leading-tight">Create account</span>
            </div>
            <div className="flex-1 h-px bg-[var(--funnel-cs-border)] mx-2" />
            <div className="flex flex-col items-center flex-1">
              <div className="h-5 w-5 sm:h-8 sm:w-8 rounded-full border-2 border-[var(--funnel-cs-accent)] text-[var(--funnel-cs-accent)] font-semibold flex items-center justify-center text-[10px] sm:text-inherit">
                2
              </div>
              <span className="mt-0.5 sm:mt-1 text-[var(--funnel-cs-ink)] font-semibold leading-tight">Add offer</span>
            </div>
            <div className="flex-1 h-px bg-[var(--funnel-cs-border)] mx-2" />
            <div className="flex flex-col items-center flex-1">
              <div className="h-5 w-5 sm:h-8 sm:w-8 rounded-full border border-[var(--funnel-cs-border)] flex items-center justify-center text-[var(--funnel-cs-muted)] font-semibold text-[10px] sm:text-inherit">
                3
              </div>
              <span className="mt-0.5 sm:mt-1 leading-tight">Get the app</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="px-5 pt-1.5 pb-3 sm:pt-2 sm:pb-4 text-center">
          <h1 className="font-[var(--funnel-cs-font-heading)] text-3xl sm:text-3xl font-semibold text-[var(--funnel-cs-ink)] leading-tight">
            Exclusive sign-up offer
          </h1>
        </div>

        {/* Offers */}
        <div className="px-4 space-y-2 sm:space-y-3">
          {offers.map((offer) => {
            const selectedState = selected === offer.id;
            return (
              <button
                key={offer.id}
                onClick={() => handleOfferSelect(offer.id)}
                className={`w-full text-left rounded-[var(--funnel-cs-radius-block)] border transition shadow-[var(--funnel-cs-shadow-soft)] ${
                  selectedState ? "border-[var(--funnel-cs-accent)] bg-[var(--funnel-cs-accent-soft)]" : "border-[var(--funnel-cs-border)] bg-[var(--funnel-cs-surface)]"
                }`}
              >
                <div className="flex items-center p-2 sm:p-3 gap-3">
                  <div
                    className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: offer.iconBg }}
                  >
                    {offer.image ? (
                      <img
                        src={offer.image}
                        alt=""
                        className="h-full w-full object-cover rounded-xl"
                      />
                    ) : (
                      <>
                        <offer.Icon size={24} className="sm:hidden" color={offer.iconColor} strokeWidth={2} />
                        <offer.Icon size={28} className="hidden sm:block" color={offer.iconColor} strokeWidth={2} />
                      </>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm sm:text-base font-semibold text-[var(--funnel-cs-ink)] leading-snug line-clamp-2">
                        {offer.title}
                      </p>
                      <IndicatorIcon selected={selectedState} />
                    </div>
                    <div className="flex items-center flex-wrap gap-2 mt-1">
                      <span className="text-sm text-[var(--funnel-cs-muted)] line-through">
                        {offer.oldPrice}
                      </span>
                      <span className="text-sm font-semibold text-[var(--funnel-cs-accent)]">
                        {offer.newPrice}
                      </span>
                      <span className="text-[11px] font-semibold text-[var(--funnel-cs-accent)] border border-[var(--funnel-cs-accent-border)] bg-[var(--funnel-cs-accent-soft)] rounded-full px-2 py-[2px]">
                        {offer.badge}
                      </span>
                    </div>
                    {offer.description ? (
                      <p className="text-xs text-[var(--funnel-cs-text)] mt-0.5 sm:mt-1 line-clamp-1">
                        {offer.description}
                      </p>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="px-5 py-3 sm:py-5 space-y-2 sm:space-y-3">
          {benefits.map((benefit, idx) => (
            <div key={idx} className="flex items-start gap-2 sm:gap-3">
              <div className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 rounded-full bg-[var(--funnel-cs-accent-soft)] text-[var(--funnel-cs-accent)] flex items-center justify-center text-[10px] sm:text-xs font-bold mt-[2px]">
                ✓
              </div>
              <p className="text-sm sm:text-base text-[var(--funnel-cs-ink)] leading-snug text-left">{benefit}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="px-5 pb-3 sm:pb-4">
          <button
            onClick={handleGetOffer}
            disabled={loading}
            className={`w-full h-12 sm:h-[54px] rounded-[var(--funnel-cs-radius-field)] bg-[var(--funnel-cs-accent)] text-[var(--funnel-cs-on-accent)] font-semibold text-sm sm:text-base hover:bg-[var(--funnel-cs-accent-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--funnel-cs-accent)] focus:ring-offset-2 focus:ring-offset-[var(--funnel-cs-surface)] transition ${
              loading ? "opacity-70 pointer-events-none" : ""
            }`}
          >
            {loading ? "Processing..." : "Get the Offer"}
          </button>
          <button
            onClick={handleSkip}
            className="mt-2 sm:mt-3 w-full text-xs sm:text-sm font-semibold text-[var(--funnel-cs-ink)] hover:text-[var(--funnel-cs-accent)] transition"
          >
            Skip the offer and continue
          </button>
        </div>

        {/* Footer */}
        <div className="border-t border-[var(--funnel-cs-border)] px-5 py-3 sm:py-4 text-xs sm:text-sm text-[var(--funnel-cs-muted)] flex items-center justify-center gap-4">
          <a
            href="https://my-stylist.pro/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[var(--funnel-cs-accent)] font-medium"
          >
            Privacy
          </a>
          <span className="text-[var(--funnel-cs-border)]">|</span>
          <a
            href="https://my-stylist.pro/terms#subscription"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[var(--funnel-cs-accent)] font-medium"
          >
            Terms
          </a>
        </div>
      </div>
    </div>
  );
};

export default GetOffer;
